import { FetchError, getRequest } from "@decibeltrade/sdk";
import { type FetchQueryOptions, useQuery } from "@tanstack/react-query";

import { PREDEPOSIT_USDC_REWARDS_CACHE_KEY } from "@/constants/local-storage-keys";
import { readDexSdk } from "@/contexts/dex-sdk";
import { getClosedCache, setClosedCache } from "@/hooks/storage/predeposit-closed-cache";

import { type PredepositUsdcRewards, PredepositUsdcRewardsSchema } from "./types";

const PREDEPOSIT_USDC_REWARDS_QUERY_KEY = "predeposit-usdc-rewards";

export function predepositUsdcRewardsQueryKey(accountAddress: string) {
  return [PREDEPOSIT_USDC_REWARDS_QUERY_KEY, accountAddress] as const;
}

async function fetchPredepositUsdcRewards(
  accountAddress: string,
): Promise<PredepositUsdcRewards | null> {
  const cached = getClosedCache(PREDEPOSIT_USDC_REWARDS_CACHE_KEY, accountAddress) as
    | PredepositUsdcRewards
    | undefined;
  if (cached !== undefined) return cached;

  const baseUrl = readDexSdk.config.tradingHttpUrl;
  const apiKey = readDexSdk.deps.apiKey;

  try {
    const response = await getRequest<PredepositUsdcRewards>({
      schema: PredepositUsdcRewardsSchema,
      url: `${baseUrl}/api/v1/predeposits/rewards?account=${accountAddress}`,
      apiKey,
    });

    const result = response.data;

    setClosedCache(PREDEPOSIT_USDC_REWARDS_CACHE_KEY, result, accountAddress);

    return result;
  } catch (error) {
    if (error instanceof FetchError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function predepositUsdcRewardsQuery(accountAddr: string) {
  return {
    queryKey: predepositUsdcRewardsQueryKey(accountAddr),
    queryFn: () => fetchPredepositUsdcRewards(accountAddr),
  } satisfies FetchQueryOptions<PredepositUsdcRewards | null>;
}

export function usePredepositUsdcRewards(accountAddress: string | undefined) {
  return useQuery({
    queryKey: predepositUsdcRewardsQueryKey(accountAddress ?? ""),
    queryFn: async () => {
      if (!accountAddress) throw new Error("Account address is required");
      return fetchPredepositUsdcRewards(accountAddress);
    },
    enabled: !!accountAddress,
    staleTime: Infinity,
    gcTime: Infinity,
    retry(failureCount, error) {
      if (error instanceof FetchError && error.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
